import { useState, useMemo, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useMachineQueue } from '../../features/jobs/queues/hooks/useQueues.js';
import { redisFetch } from '../../features/jobs/api/http';

// The target's merge group = the target job + every queue job sharing a token with
// it. Group-by/individual jobs resolve to just [target].
function mergeGroupOf(targetJobId, jobs) {
  const target = (jobs || []).find(j => String(j.jobId) === String(targetJobId));
  if (!target) return targetJobId ? [String(targetJobId)] : [];
  const tgtTokens = new Set(
    (target.queue || []).flatMap(t => (t.outputs || []).map(o => o?.tokenId)).filter(Boolean)
  );
  const group = new Set([String(targetJobId)]);
  for (const j of jobs || []) {
    if (String(j.jobId) === String(targetJobId)) continue;
    if ((j.queue || []).some(t => (t.outputs || []).some(o => tgtTokens.has(o?.tokenId)))) {
      group.add(String(j.jobId));
    }
  }
  return [...group];
}

// Put a FINALIZED execution back in the queue as PENDING (correcting a mistaken
// completion). producedLength resets to 0; the old ExecutionLog is flagged as
// corrected; the drum can be re-queued to the same or a corrected target product.
const RequeueExecutionModal = ({ show, onHide, log, machineId, onDone }) => {
  const { data } = useMachineQueue(machineId);
  const queueJobs = data?.groups || [];

  const targetOptions = useMemo(() => {
    const opts = queueJobs.map(g => ({ jobId: g.jobId, productLabel: g.productLabel, queue: g.queue }));
    if (log && !opts.some(o => String(o.jobId) === String(log.jobId))) {
      opts.unshift({ jobId: log.jobId, productLabel: log.productLabel, queue: [] });
    }
    return opts;
  }, [queueJobs, log]);

  const [targetJobId, setTargetJobId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (show && log) {
      setTargetJobId(String(log.jobId || ''));
      setPreview(null);
      setErr(null);
    }
  }, [show, log]);

  const targetJobIds = useMemo(
    () => (targetJobId ? mergeGroupOf(targetJobId, targetOptions) : []),
    [targetJobId, targetOptions]
  );
  const isMerge = targetJobIds.length > 1;

  const run = async dryRun => {
    setErr(null);
    setBusy(true);
    try {
      const res = await redisFetch(
        `/api/redis/queues/${machineId}/requeue-execution`,
        {
          method: 'POST',
          body: JSON.stringify({
            source: { jobId: log.jobId, taskId: log.taskId, tokenId: log.tokenId },
            executionLogId: log._id,
            targetJobIds,
            dryRun
          })
        }
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      if (dryRun) setPreview(d);
      else onDone?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  if (!log) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">Re-queue execution as PENDING</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fs-9 mb-3">
          Finalized drum: <b>{log.productLabel}</b> · {log.phase} · {log.color || '-'}{' '}
          · <span className="font-monospace text-700">{log.tokenId}</span> ·{' '}
          {log.plannedLength}m
        </div>

        <Form.Group className="mb-2">
          <Form.Label className="fs-9 fw-semibold text-700">
            Re-queue to product:
          </Form.Label>
          <Form.Select
            size="sm"
            value={targetJobId}
            onChange={e => {
              setTargetJobId(e.target.value);
              setPreview(null);
            }}
          >
            {targetOptions.map(j => (
              <option key={j.jobId} value={j.jobId}>
                {j.productLabel || j.jobId} ({String(j.jobId).slice(0, 6)})
                {String(j.jobId) === String(log.jobId) ? ' — same' : ''}
              </option>
            ))}
          </Form.Select>
          {isMerge && (
            <div className="text-info fs-10 mt-1">
              Merge target → shared PENDING drum in {targetJobIds.length} products.
            </div>
          )}
        </Form.Group>

        {err && (
          <Alert variant="danger" className="fs-9 mb-2">
            {err}
          </Alert>
        )}

        {preview && (
          <Alert variant="info" className="fs-9 mb-0">
            <div className="fw-semibold mb-1">Preview (nothing written yet):</div>
            <div>
              → remove DONE drum from <b>{preview.source?.product}</b>; new PENDING
              drum (0m produced){preview.shared ? ' shared' : ''} at{' '}
              <b>{(preview.created || []).map(c => c.product).join(' + ')}</b>
            </div>
            <div>
              → {preview.executionLogsFlagged} history record(s) flagged as corrected
            </div>
            <div className="text-700 fs-10 mt-1">
              producedLength reset to 0; queue order preserved.
            </div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" className="text-600" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="outline-primary"
          disabled={busy || !targetJobId}
          onClick={() => run(true)}
        >
          {busy ? '...' : 'Preview'}
        </Button>
        <Button
          variant="primary"
          disabled={busy || !targetJobId || !preview}
          onClick={() => run(false)}
        >
          {busy ? 'Applying...' : 'Re-queue'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RequeueExecutionModal;
